import React from "react";
import PropTypes from "prop-types";
import Datatable from "../Datatable";
import SearchText from "../SearchText";
import { SENTIMENT, B2B_VALUE_DICT } from "../../modules/drivers";
import { AGE, GENDER, INCOME, EDUCATION, SEGMENT_DICT } from "./QuoteCell";
import * as styles from "../../styles/components/DriverQuotesTable.css";

const SENTIMENT_BY_DRIVER = {
    Credibility: {
        positive: "Credible",
        negative: "Not Credible"
    },
    Appeal: {
        positive: "Appealing",
        negative: "Not Appealing"
    },
    Relevance: {
        positive: "Relevant",
        negative: "Not Relevant"
    },
    Clarity: {
        positive: "Clear",
        negative: "Not Clear"
    },
    Experience: {
        positive: "Good Experience",
        negative: "Poor Experience"
    }
};

function QuoteCell({ driverQuote }) {
    const { quote, matchIndices } = driverQuote;
    return (
        <div className={styles.quoteCell}>
            <SearchText text={quote} matchIndices={matchIndices} />
        </div>
    );
}

QuoteCell.propTypes = {
    driverQuote: PropTypes.shape({
        quote: PropTypes.string.isRequired,
        matchIndices: PropTypes.arrayOf(
            PropTypes.arrayOf(PropTypes.number.isRequired)
        )
    })
};

const EmptyCell = () => <span className={styles.demographicCell}> - </span>;

function DemographicCell({ segmentId }) {
    const segment = SEGMENT_DICT[segmentId];
    return <span className={styles.demographicCell}>{segment.label}</span>;
}

DemographicCell.propTypes = {
    segmentId: PropTypes.number.isRequired
};

function B2BCell({ b2bResponse }) {
    const b2bLabel = Object.keys(B2B_VALUE_DICT).find(key =>
        B2B_VALUE_DICT[key].options.includes(b2bResponse)
    );
    return <span className={styles.demographicCell}>{b2bLabel}</span>;
}

B2BCell.propTypes = {
    b2bResponse: PropTypes.string.isRequired
};

function SentimentCell({ driver, sentiment }) {
    const label = SENTIMENT_BY_DRIVER[driver][sentiment];
    return (
        <div className={styles.sentimentCell}>
            <p className={`${styles.sentiment} ${styles[sentiment]}`}>
                {label}
            </p>
        </div>
    );
}

SentimentCell.propTypes = {
    driver: PropTypes.string.isRequired,
    sentiment: PropTypes.oneOf([SENTIMENT.positive, SENTIMENT.negative])
        .isRequired
};

function createColumns(showSentiment, showB2b) {
    return showB2b
        ? [
              Datatable.createColumn({
                  renderHeaderCell: () => <h5>Quote</h5>,
                  renderBodyCell: driverQuote => (
                      <QuoteCell driverQuote={driverQuote} />
                  ),
                  cellWidthPercentage: showSentiment ? 50 : 65
              }),
              Datatable.createColumn({
                  renderHeaderCell: () => <h5>Business Age</h5>,
                  renderBodyCell: driverQuote =>
                      driverQuote.DEM_B2B_Age ? (
                          <B2BCell b2bResponse={driverQuote.DEM_B2B_Age} />
                      ) : (
                          <EmptyCell />
                      ),
                  cellWidthPercentage: 11,
                  comparator: (a, b) => {
                      if (!(a.DEM_B2B_Age || b.DEM_B2B_Age)) {
                          return 0;
                      }
                      if (!a.DEM_B2B_Age) {
                          return -1;
                      }
                      if (!b.DEM_B2B_Age) {
                          return 1;
                      }
                      const aKey = Object.keys(B2B_VALUE_DICT).find(key =>
                          B2B_VALUE_DICT[key].options.includes(a.DEM_B2B_Age)
                      );
                      const bKey = Object.keys(B2B_VALUE_DICT).find(key =>
                          B2B_VALUE_DICT[key].options.includes(b.DEM_B2B_Age)
                      );
                      return (
                          B2B_VALUE_DICT[aKey].sortOrder -
                          B2B_VALUE_DICT[bKey].sortOrder
                      );
                  }
              }),
              Datatable.createColumn({
                  renderHeaderCell: () => <h5>Revenue</h5>,
                  renderBodyCell: driverQuote =>
                      driverQuote.DEM_B2B_Revenue ? (
                          <B2BCell b2bResponse={driverQuote.DEM_B2B_Revenue} />
                      ) : (
                          <EmptyCell />
                      ),
                  cellWidthPercentage: 8,
                  comparator: (a, b) => {
                      if (!(a.DEM_B2B_Revenue || b.DEM_B2B_Revenue)) {
                          return 0;
                      }
                      if (!a.DEM_B2B_Revenue) {
                          return -1;
                      }
                      if (!b.DEM_B2B_Revenue) {
                          return 1;
                      }
                      const aKey = Object.keys(B2B_VALUE_DICT).find(key =>
                          B2B_VALUE_DICT[key].options.includes(
                              a.DEM_B2B_Revenue
                          )
                      );
                      const bKey = Object.keys(B2B_VALUE_DICT).find(key =>
                          B2B_VALUE_DICT[key].options.includes(
                              b.DEM_B2B_Revenue
                          )
                      );
                      return (
                          B2B_VALUE_DICT[aKey].sortOrder -
                          B2B_VALUE_DICT[bKey].sortOrder
                      );
                  }
              }),
              Datatable.createColumn({
                  renderHeaderCell: () => <h5>Role</h5>,
                  renderBodyCell: driverQuote =>
                      driverQuote.DEM_B2B_Role ? (
                          <B2BCell b2bResponse={driverQuote.DEM_B2B_Role} />
                      ) : (
                          <EmptyCell />
                      ),
                  cellWidthPercentage: 15,
                  comparator: (a, b) => {
                      if (!(a.DEM_B2B_Role || b.DEM_B2B_Role)) {
                          return 0;
                      }
                      if (!a.DEM_B2B_Role) {
                          return -1;
                      }
                      if (!b.DEM_B2B_Role) {
                          return 1;
                      }
                      const aKey = Object.keys(B2B_VALUE_DICT).find(key =>
                          B2B_VALUE_DICT[key].options.includes(a.DEM_B2B_Role)
                      );
                      const bKey = Object.keys(B2B_VALUE_DICT).find(key =>
                          B2B_VALUE_DICT[key].options.includes(b.DEM_B2B_Role)
                      );
                      return (
                          B2B_VALUE_DICT[aKey].sortOrder -
                          B2B_VALUE_DICT[bKey].sortOrder
                      );
                  }
              }),
              Datatable.createColumn({
                  renderHeaderCell: () => <h5>Company Size</h5>,
                  renderBodyCell: driverQuote =>
                      driverQuote.DEM_B2B_Size ? (
                          <B2BCell b2bResponse={driverQuote.DEM_B2B_Size} />
                      ) : (
                          <EmptyCell />
                      ),
                  cellWidthPercentage: 12,
                  comparator: (a, b) => {
                      if (!(a.DEM_B2B_Size || b.DEM_B2B_Size)) {
                          return 0;
                      }
                      if (!a.DEM_B2B_Size) {
                          return -1;
                      }
                      if (!b.DEM_B2B_Size) {
                          return 1;
                      }
                      const aKey = Object.keys(B2B_VALUE_DICT).find(key =>
                          B2B_VALUE_DICT[key].options.includes(a.DEM_B2B_Size)
                      );
                      const bKey = Object.keys(B2B_VALUE_DICT).find(key =>
                          B2B_VALUE_DICT[key].options.includes(b.DEM_B2B_Size)
                      );
                      return (
                          B2B_VALUE_DICT[aKey].sortOrder -
                          B2B_VALUE_DICT[bKey].sortOrder
                      );
                  }
              }),
              Datatable.createColumn({
                  disabled: !showSentiment,
                  renderHeaderCell: () => <h5>Sentiment</h5>,
                  renderBodyCell: driverQuote => (
                      <SentimentCell
                          driver={driverQuote.driver}
                          sentiment={driverQuote.sentiment}
                      />
                  ),
                  cellWidthPercentage: 15,
                  comparator: "sentiment"
              })
          ]
        : [
              Datatable.createColumn({
                  renderHeaderCell: () => <h5>Quote</h5>,
                  renderBodyCell: driverQuote => (
                      <QuoteCell driverQuote={driverQuote} />
                  ),
                  cellWidthPercentage: showSentiment ? 50 : 65
              }),
              Datatable.createColumn({
                  renderHeaderCell: () => <h5>Gender</h5>,
                  renderBodyCell: driverQuote =>
                      driverQuote.gender &&
                      SEGMENT_DICT[driverQuote.gender.id] ? (
                          <DemographicCell segmentId={driverQuote.gender.id} />
                      ) : (
                          <EmptyCell />
                      ),
                  cellWidthPercentage: 6,
                  comparator: (a, b) => {
                      if (!(a.gender || b.gender)) {
                          return 0;
                      }
                      if (!a.gender) {
                          return -1;
                      }
                      if (!b.gender) {
                          return 1;
                      }
                      return a.gender.sortOrder - b.gender.sortOrder;
                  }
              }),
              Datatable.createColumn({
                  renderHeaderCell: () => <h5>Age</h5>,
                  renderBodyCell: driverQuote =>
                      driverQuote.age && SEGMENT_DICT[driverQuote.age.id] ? (
                          <DemographicCell segmentId={driverQuote.age.id} />
                      ) : (
                          <EmptyCell />
                      ),
                  cellWidthPercentage: 6,
                  comparator: (a, b) => {
                      if (!(a.age || b.age)) {
                          return 0;
                      }
                      if (!a.age) {
                          return -1;
                      }
                      if (!b.age) {
                          return 1;
                      }
                      return a.age.sortOrder - b.age.sortOrder;
                  }
              }),
              Datatable.createColumn({
                  renderHeaderCell: () => <h5>Income</h5>,
                  renderBodyCell: driverQuote =>
                      driverQuote.income &&
                      SEGMENT_DICT[driverQuote.income.id] ? (
                          <DemographicCell segmentId={driverQuote.income.id} />
                      ) : (
                          <EmptyCell />
                      ),
                  cellWidthPercentage: 8,
                  comparator: (a, b) => {
                      if (!(a.income || b.income)) {
                          return 0;
                      }
                      if (!a.income) {
                          return -1;
                      }
                      if (!b.income) {
                          return 1;
                      }
                      return a.income.sortOrder - b.income.sortOrder;
                  }
              }),
              Datatable.createColumn({
                  renderHeaderCell: () => <h5>Education</h5>,
                  renderBodyCell: driverQuote =>
                      driverQuote.education &&
                      SEGMENT_DICT[driverQuote.education.id] ? (
                          <DemographicCell
                              segmentId={driverQuote.education.id}
                          />
                      ) : (
                          <EmptyCell />
                      ),
                  cellWidthPercentage: 15,
                  comparator: (a, b) => {
                      if (!(a.education || b.education)) {
                          return 0;
                      }
                      if (!a.education) {
                          return -1;
                      }
                      if (!b.education) {
                          return 1;
                      }
                      return a.education.sortOrder - b.education.sortOrder;
                  }
              }),
              Datatable.createColumn({
                  disabled: !showSentiment,
                  renderHeaderCell: () => <h5>Sentiment</h5>,
                  renderBodyCell: driverQuote => (
                      <SentimentCell
                          driver={driverQuote.driver}
                          sentiment={driverQuote.sentiment}
                      />
                  ),
                  cellWidthPercentage: 15,
                  comparator: "sentiment"
              })
          ];
}

export default function DriverQuotesTable({
    driverQuotes,
    showSentiment,
    showB2b
}) {
    driverQuotes = driverQuotes.map(quote => {
        const gender = quote.segmentIds.find(
            segment => segment && segment.audienceId === GENDER.id
        );
        const age = quote.segmentIds.find(
            segment => segment && segment.audienceId === AGE.id
        );
        const income = quote.segmentIds.find(
            segment => segment && segment.audienceId === INCOME.id
        );
        const education = quote.segmentIds.find(
            segment => segment && segment.audienceId === EDUCATION.id
        );
        return {
            ...quote,
            driver: quote.clusterType,
            gender,
            age,
            income,
            education
        };
    });

    return (
        <Datatable
            className={styles.driverQuoteTable}
            columns={createColumns(showSentiment, showB2b)}
            data={driverQuotes}
            scroll={{ height: 750 }}
        >
            {driverQuotes.length === 0 && (
                <p key="no-quote-message" className={styles.noQuoteMessage}>
                    Sorry! There are no quotes to display.
                </p>
            )}
        </Datatable>
    );
}

DriverQuotesTable.propTypes = {
    showSentiment: PropTypes.bool.isRequired,
    driverQuotes: PropTypes.arrayOf(
        PropTypes.shape({
            quote: PropTypes.string.isRequired,
            clusterType: PropTypes.string.isRequired,
            sentiment: PropTypes.oneOf([
                SENTIMENT.positive,
                SENTIMENT.negative
            ]),
            segmentIds: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    audienceId: PropTypes.number.isRequired
                })
            ).isRequired,
            matchIndices: PropTypes.arrayOf(
                PropTypes.arrayOf(PropTypes.number.isRequired)
            )
        })
    ).isRequired
};



// WEBPACK FOOTER //
// ./src/components/Results/DriverQuotesTable.js